<template>
  <ResponsiveTable>
    <thead>
      <tr>
        <th>ID</th>
        <th>Client</th>
        <th>Country</th>
        <th>Qty</th>
        <th>Order</th>
        <th>Actions</th>
        <th>Created at</th>
        <th>Updated at</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="form in forms" :key="form.id">
        <td>
          <small>{{ form.id }}</small>
        </td>
        <td>
          {{ form.client.name }}
          <br>
          <small>
            {{ form.client.email }}
            <br>
            {{ displayPhone(form.client.phone) }}
          </small>
        </td>
        <td>
          <CountryFlag :code="getVisaCountryIsoCode(form.country) "/>
          <br>
          <small>{{ getVisaCountryName(form.country) }}</small>
        </td>
        <td>{{ form.quantity }}</td>
        <td>
          <AppLink v-if="form.orderId" :to="`order/${form.orderId}`">
            {{ form.orderId }}
          </AppLink>
          <ProgressBar v-else-if="isNumber(form.meta?.completion)" :model-value="form.meta.completion">
            {{ form.meta.completion * 100 }}%
          </ProgressBar>
          <span v-else>-</span>
        </td>
        <td>
          <v-menu>
            <template #activator="{ props }">
              <AppBtn icon="mdi:more-horiz" v-bind="props" />
            </template>
            <v-list :disabled="busyId == form.id">
              <v-list-item @click="$emit('inspect', form.id)">
                <v-list-item-title>
                  <AppIcon icon="mdi:magnify" start />
                  Inspect
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('send', form.id)">
                <v-list-item-title>
                  <AppIcon icon="mdi:email-outline" start />
                  Pull
                </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="isFormPayed(form)" @click="$emit('resume', form.id)">
                <v-list-item-title>
                  <AppIcon icon="mdi:archive-edit-outline" start />
                  Resume
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('duplicate', form.id)">
                <v-list-item-title>
                  <AppIcon icon="mdi:content-duplicate" start />
                  Duplicate
                </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="isFormPayed(form)" @click="$emit('create-order', form.id)">
                <v-list-item-title>
                  <AppIcon icon="mdi:attach-money" start />
                  New Order
                </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="isFormPayed(form)" @click="$emit('delete', form.id)">
                <v-list-item-title>
                  <TrashIcon start />
                  Delete
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </td>
        <td>
          {{ formatDatetime(form.created_at) }}
          <br>
          <small>{{ getTimeAgoText(form.created_at) }}</small>
        </td>
        <td>
          <template v-if="form.updated_at">
            {{ formatDatetime(form.updated_at) }}
            <br>
            <small>{{ getTimeAgoText(form.updated_at) }}</small>
          </template>
          <span v-else>-</span>
        </td>
      </tr>
    </tbody>
  </ResponsiveTable>
</template>

<script setup lang="ts">
import { formatDatetime, formatPhone } from '~/helpers/format';
import { isNumber } from '~/helpers/general';
import { getVisaCountryIsoCode, getVisaCountryName } from '~/helpers/visa-country';
import { getTimeAgoText } from '~/helpers/format';
import type { SerializedDb, VisaForm, Phone } from '~/types';
import { isFormPayed } from '~/helpers/visa-form';

defineProps<{
  forms: SerializedDb<VisaForm>[];
  busyId: VisaForm['id'] | null;
}>();

function displayPhone(phone: Phone) {
  return typeof phone == 'string' ? phone : formatPhone(phone);
}

defineEmits<{
  'inspect': [VisaForm['id']];
  'resume': [VisaForm['id']];
  'send': [VisaForm['id']];
  'duplicate': [VisaForm['id']];
  'create-order': [VisaForm['id']];
  'delete': [VisaForm['id']];
}>();
</script>